/**
 * ME — INSPETOR RESPONSÁVEL E A CASCATA PARA A SUB-OS.
 *
 * Duas regras que antes estavam só na tela, e por isso não valiam nada:
 *
 *   1. QUEM PODE ESCOLHER O INSPETOR. Quem executa o ensaio não escolhe: o
 *      Inspetor Responsável do seu relatório é ele mesmo, resolvido pelo
 *      Professional da conta logada. Designar outra pessoa é ato de
 *      Engenharia/Administração e exige `MeReportAssignInspector`. Sem isso,
 *      qualquer um emitiria um laudo em nome de terceiro.
 *
 *   2. QUAIS SUB-OS APARECEM. Só as Sub-OS cuja RESPONSABILIDADE é do
 *      Inspetor escolhido — `SubWorkOrder.professionalId`, a fonte canônica
 *      única da seção 26 do CLAUDE.md.
 *
 *      É PROIBIDO resolver isto por `ProfessionalSubOsAccess`: aquilo são os
 *      COLABORADORES do RDO, vários por Sub-OS, e usá-los aqui faria o
 *      documento apontar como responsável alguém que só tinha acesso
 *      operacional. O RDO continua enxergando pelos dois vínculos; o
 *      documento, não.
 *
 * Tudo aqui é função pura: a mesma regra roda na tela e no serviço, e é o
 * serviço que decide — esconder um `<select>` nunca foi controle de nada.
 */

// Direto de `domain/identity`, a origem real do enum. `permissions/permissions.ts`
// é só um re-export dele, e importá-lo daqui faria o domínio depender "para
// fora" — fronteira verificada por tests/architecture-boundaries.test.ts.
import { PermissionCode } from "../identity/access-types.js";

/** O mínimo que este módulo precisa saber de uma Sub-OS. */
export interface SubOsLike {
  readonly id: string;
  readonly code?: string;
  readonly description?: string | null;
  /** RESPONSÁVEL da Sub-OS — fonte canônica e única. */
  readonly professionalId?: string | null;
}

/**
 * Pode designar um Inspetor diferente de si mesmo?
 *
 * Recebe as permissões já resolvidas da sessão, não o nome do perfil:
 * decidir por `role === "ADMIN"` é exatamente o atalho que o manual proíbe.
 */
export function canAssignInspector(permissions: readonly string[] | undefined | null): boolean {
  if (!Array.isArray(permissions)) return false;
  return permissions.includes(PermissionCode.MeReportAssignInspector);
}

/**
 * O Inspetor que o relatório deve assumir sozinho quando o usuário não pode
 * designar outro: o Professional da conta logada. `null` quando a conta não
 * tem Professional vinculado — aí não há o que assumir, e a tela precisa
 * dizer isso em vez de deixar o campo em branco sem explicação.
 */
export function automaticInspectorId(
  userProfessionalId: string | null | undefined
): string | null {
  const id = (userProfessionalId ?? "").trim();
  return id === "" ? null : id;
}

/** As Sub-OS sob responsabilidade deste Professional, e só elas. */
export function subOsForInspector(
  subWorkOrders: readonly SubOsLike[],
  inspectorProfessionalId: string | null | undefined
): readonly SubOsLike[] {
  const inspector = (inspectorProfessionalId ?? "").trim();
  if (!inspector) return [];
  return subWorkOrders.filter(sub => String(sub.professionalId ?? "").trim() === inspector);
}

/**
 * Como a tela deve se comportar diante do conjunto de Sub-OS do Inspetor.
 *
 *   NO_INSPECTOR  ainda não há Inspetor — nada a oferecer
 *   NONE          o Inspetor não responde por nenhuma Sub-OS desta OS
 *   AUTO          exatamente uma: é selecionada sozinha
 *   CHOOSE        várias: o usuário escolhe, mas só entre as dele
 */
export type MeSubOsMode = "NO_INSPECTOR" | "NONE" | "AUTO" | "CHOOSE";

export interface MeSubOsResolution {
  readonly mode: MeSubOsMode;
  readonly options: readonly SubOsLike[];
  /** A Sub-OS que deve ficar selecionada; null quando não há uma definida. */
  readonly selectedId: string | null;
  /** Mensagem para a tela quando não dá para seguir. Vazia nos demais casos. */
  readonly message: string;
}

/**
 * Resolve a Sub-OS a partir do Inspetor.
 *
 * Uma seleção anterior só é preservada se ainda pertencer ao Inspetor atual.
 * Trocar de Inspetor precisa LIMPAR a Sub-OS: mantê-la deixaria o documento
 * apontando para uma Sub-OS de outra pessoa, que é o vínculo errado sem
 * nenhum aviso.
 */
export function resolveMeSubOs(
  subWorkOrders: readonly SubOsLike[],
  inspectorProfessionalId: string | null | undefined,
  currentSubOsId: string | null | undefined
): MeSubOsResolution {
  const inspector = (inspectorProfessionalId ?? "").trim();
  if (!inspector) {
    return {
      mode: "NO_INSPECTOR",
      options: [],
      selectedId: null,
      message: "Selecione o Inspetor Responsável para listar as Sub-OS sob responsabilidade dele."
    };
  }

  const options = subOsForInspector(subWorkOrders, inspector);

  if (options.length === 0) {
    return {
      mode: "NONE",
      options: [],
      selectedId: null,
      message: "O Inspetor selecionado não é responsável por nenhuma Sub-OS desta Ordem de Serviço. Ajuste a responsabilidade no cadastro de Sub-OS ou escolha outro Inspetor."
    };
  }

  if (options.length === 1) {
    return { mode: "AUTO", options, selectedId: options[0]!.id, message: "" };
  }

  const current = (currentSubOsId ?? "").trim();
  const stillValid = current !== "" && options.some(o => o.id === current);
  return { mode: "CHOOSE", options, selectedId: stillValid ? current : null, message: "" };
}

/**
 * Gate de servidor: a Sub-OS gravada pertence mesmo ao Inspetor do relatório?
 *
 * A tela já filtra, mas filtrar é desenho. Uma chamada direta pode mandar
 * qualquer par, e é aqui que ela é recusada.
 *
 * Relatório sem Sub-OS é válido — nem todo ME tem — então a ausência não é
 * violação; o que não pode é uma Sub-OS de outra pessoa.
 */
export function isSubOsCompatibleWithInspector(
  subWorkOrders: readonly SubOsLike[],
  inspectorProfessionalId: string | null | undefined,
  subOsId: string | null | undefined
): boolean {
  const target = (subOsId ?? "").trim();
  if (target === "") return true;

  const sub = subWorkOrders.find(s => s.id === target);
  // Sub-OS desconhecida neste conjunto: não é deste módulo decidir que sumiu.
  if (!sub) return true;

  const inspector = (inspectorProfessionalId ?? "").trim();
  return String(sub.professionalId ?? "").trim() === inspector;
}
